// Pure text-range finder for allergen substance evidence (art. 21 §1 lett. b
// Reg. UE 1169/2011): the substance name must be evidenced in the ingredient
// list "attraverso un tipo di carattere chiaramente distinto ... per esempio
// per dimensioni, stile o colore di sfondo". This file only finds WHERE the
// substance is mentioned in an already-composed line — it never mutates the
// text and the renderer (frontend, PDF) never re-searches it: a wrong match
// would bold the wrong word on food-safety information.
//
// `composeIngredientListLine` (`ingredient-list-text.ts`) is the only intended
// caller, shared by the backend label surfaces and the frontend live preview.
// It first composes the line text (appending "(<substanceName>)" when the
// substance is not already mentioned, e.g. "besciamella" -> "besciamella
// (Latte)"), then calls `findAllergenEmphasisRanges` once on the *final* text
// so natural mentions ("farina di grano" already contains "grano") and
// appended ones are located in a single pass with correct offsets.

export interface AllergenEmphasisTarget {
  /** Canonical substance name plus any accepted synonym forms, tried in order. */
  names: readonly string[];
}

export interface TextEmphasisRange {
  start: number;
  end: number;
}

/**
 * Finds the first case-insensitive, whole-word occurrence of each target's
 * name (or one of its synonyms) inside `text`. A target with no match is
 * silently omitted from the result: this function never guesses a range that
 * is not actually there, and never mutates `text`.
 *
 * Word boundaries are Unicode-letter-aware (`\p{L}`/`\p{N}` lookaround), not
 * the ASCII-only `\b`, for two reasons: accented letters are not `\w`
 * characters in JavaScript, so `\b` can split a boundary mid-word; and a
 * substring match without any boundary check would wrongly flag unrelated
 * words that merely contain the substance name, e.g. matching the wheat
 * substance "grano" inside "granoturco" (maize, not wheat) or "sgranare".
 *
 * Results are sorted by `start` so the output is deterministic regardless of
 * the order `targets` were supplied in — this feeds the confirmation payload
 * hash (`label-payload.builder.ts`, Wave 3), which must be stable for the
 * same logical input.
 */
export function findAllergenEmphasisRanges(
  text: string,
  targets: readonly AllergenEmphasisTarget[],
): TextEmphasisRange[] {
  const ranges: TextEmphasisRange[] = [];
  for (const target of targets) {
    const range = findFirstMatch(text, target.names);
    if (range) {
      ranges.push(range);
    }
  }
  return ranges.sort((left, right) => left.start - right.start);
}

function findFirstMatch(text: string, names: readonly string[]): TextEmphasisRange | null {
  for (const name of names) {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      continue;
    }
    const match = buildWordBoundaryRegex(trimmedName).exec(text);
    if (match) {
      return { start: match.index, end: match.index + match[0].length };
    }
  }
  return null;
}

function buildWordBoundaryRegex(term: string): RegExp {
  const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![\\p{L}\\p{N}])${escapedTerm}(?![\\p{L}\\p{N}])`, 'iu');
}

/**
 * Aggiunge "(<sostanza>)" per ogni sostanza CONTAINS non gia' leggibile nel
 * testo, poi si ferma: `findAllergenEmphasisRanges` va chiamata una sola volta
 * a valle, sul testo definitivo, cosi' gli intervalli puntano sempre alla
 * stringa che il chiamante riceve davvero.
 */
export function appendMissingAllergenEvidence(
  baseText: string,
  containsTargets: readonly AllergenEmphasisTarget[],
): string {
  let text = baseText;
  for (const target of containsTargets) {
    const alreadyEvidenced = findAllergenEmphasisRanges(text, [target]).length > 0;
    if (!alreadyEvidenced) {
      text = `${text} (${target.names[0]})`;
    }
  }
  return text;
}
